import Dexie, { Table } from 'dexie';
import type {
  AppSettings, Branch, Deal, Employee, GameFormat, Occasion, RefusalReason, Source, Team,
} from '@/types';
import { PREBOOKING_HOLD_MINUTES_DEFAULT } from '@/types';
import { OKK_DEFAULT_CONFIG } from '@/data/okkForm';

class CRMDatabase extends Dexie {
  branches!: Table<Branch, string>;
  employees!: Table<Employee, string>;
  teams!: Table<Team, string>;
  sources!: Table<Source, string>;
  occasions!: Table<Occasion, string>;
  game_formats!: Table<GameFormat, string>;
  refusal_reasons!: Table<RefusalReason, string>;
  deals!: Table<Deal, string>;
  settings!: Table<AppSettings, string>;

  constructor() {
    super('teamscheduler-crm');
    this.version(1).stores({
      branches: 'id, name',
      employees: 'id, full_name, branch_id, is_active',
      teams: 'id, name, branch_id, is_archived',
      sources: 'id, name',
      occasions: 'id, name',
      game_formats: 'id, name',
      refusal_reasons: 'id, name',
      deals: 'id, number, kind, status, branch_id, responsible_id, team_id, [event.start_at+branch_id], event.start_at, event.event_date, created_at',
      settings: 'id',
    });
    // v2: расширенный демо-датасет (3-6 сотрудников/филиал, командные бригады по филиалам)
    // Чистим всё, кроме настроек, чтобы seed заново отработал.
    this.version(2).stores({
      branches: 'id, name',
      employees: 'id, full_name, branch_id, is_active',
      teams: 'id, name, branch_id, is_archived',
      sources: 'id, name',
      occasions: 'id, name',
      game_formats: 'id, name',
      refusal_reasons: 'id, name',
      deals: 'id, number, kind, status, branch_id, responsible_id, team_id, [event.start_at+branch_id], event.start_at, event.event_date, created_at',
      settings: 'id',
    }).upgrade(async (tx) => {
      await tx.table('branches').clear();
      await tx.table('employees').clear();
      await tx.table('teams').clear();
      await tx.table('sources').clear();
      await tx.table('occasions').clear();
      await tx.table('game_formats').clear();
      await tx.table('refusal_reasons').clear();
      await tx.table('deals').clear();
    });
    // v3: тёмная тема по умолчанию (под эталон ShowToday) + бэкфилл новых настроек
    this.version(3).stores({
      branches: 'id, name',
      employees: 'id, full_name, branch_id, is_active',
      teams: 'id, name, branch_id, is_archived',
      sources: 'id, name',
      occasions: 'id, name',
      game_formats: 'id, name',
      refusal_reasons: 'id, name',
      deals: 'id, number, kind, status, branch_id, responsible_id, team_id, [event.start_at+branch_id], event.start_at, event.event_date, created_at',
      settings: 'id',
    }).upgrade(async (tx) => {
      const s = await tx.table('settings').get('singleton');
      if (s) {
        s.theme = 'dark';
        if (s.resource_break_minutes == null) s.resource_break_minutes = 60;
        await tx.table('settings').put(s);
      }
    });
    // v4: редактируемая анкета ОКК — бэкфилл дефолтом для старых записей настроек.
    this.version(4).stores({
      branches: 'id, name',
      employees: 'id, full_name, branch_id, is_active',
      teams: 'id, name, branch_id, is_archived',
      sources: 'id, name',
      occasions: 'id, name',
      game_formats: 'id, name',
      refusal_reasons: 'id, name',
      deals: 'id, number, kind, status, branch_id, responsible_id, team_id, [event.start_at+branch_id], event.start_at, event.event_date, created_at',
      settings: 'id',
    }).upgrade(async (tx) => {
      const s = await tx.table('settings').get('singleton');
      if (s) {
        if (s.okk_questions == null) s.okk_questions = OKK_DEFAULT_CONFIG;
        await tx.table('settings').put(s);
      }
    });
    // v5: срок «предброни» — бэкфилл дефолтом (120 мин) для старых записей настроек.
    this.version(5).stores({
      branches: 'id, name',
      employees: 'id, full_name, branch_id, is_active',
      teams: 'id, name, branch_id, is_archived',
      sources: 'id, name',
      occasions: 'id, name',
      game_formats: 'id, name',
      refusal_reasons: 'id, name',
      deals: 'id, number, kind, status, branch_id, responsible_id, team_id, [event.start_at+branch_id], event.start_at, event.event_date, created_at',
      settings: 'id',
    }).upgrade(async (tx) => {
      const s = await tx.table('settings').get('singleton');
      if (s) {
        if (s.prebooking_hold_minutes == null) s.prebooking_hold_minutes = PREBOOKING_HOLD_MINUTES_DEFAULT;
        await tx.table('settings').put(s);
      }
    });
  }
}

export const db = new CRMDatabase();

export const DEFAULT_SETTINGS: AppSettings = {
  id: 'singleton',
  theme: 'dark',
  default_currency: 'KZT',
  default_branch_id: null,
  use_branch_tz_in_dashboards: true,
  resource_break_minutes: 60,
  prebooking_hold_minutes: PREBOOKING_HOLD_MINUTES_DEFAULT,
  okk_questions: OKK_DEFAULT_CONFIG,
  bitrix: {
    enabled: false,
    auto_sync_interval_min: 0,
    read_only: false,
  },
};

export async function ensureSettings(): Promise<AppSettings> {
  const existing = await db.settings.get('singleton');
  if (existing) {
    // Бэкфилл новых полей у ранее сохранённых настроек.
    let touched = false;
    if (existing.okk_questions == null) { existing.okk_questions = OKK_DEFAULT_CONFIG; touched = true; }
    if (existing.prebooking_hold_minutes == null) {
      existing.prebooking_hold_minutes = PREBOOKING_HOLD_MINUTES_DEFAULT; touched = true;
    }
    if (touched) await db.settings.put(existing);
    return existing;
  }
  await db.settings.put(DEFAULT_SETTINGS);
  return DEFAULT_SETTINGS;
}

/** Сгенерировать новый номер заказа: B2C-2026-0001 / EVT-2026-0042. */
export async function nextDealNumber(kind: 'b2c' | 'event'): Promise<string> {
  const prefix = kind === 'event' ? 'EVT' : 'B2C';
  const year = new Date().getFullYear();
  const all = await db.deals.toArray();
  const same = all.filter((d) => d.number?.startsWith(`${prefix}-${year}-`));
  const max = same.reduce((m, d) => {
    const n = parseInt(d.number.split('-').pop() || '0', 10);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return `${prefix}-${year}-${String(max + 1).padStart(4, '0')}`;
}
